/**
 * Guest link refusals
 * A guest call refused because of the link's state (spec Appendix A): the
 * functions throw `HttpsError('permission-denied', message, { linkState,
 * maskedEmail? })`. Kept apart from guestApi so pages and tests can use the
 * mapping without loading Firebase.
 */
import { GuestLinkRefusal } from '../types/guestAccess';

const REFUSALS: readonly GuestLinkRefusal[] = ['invalid', 'expired', 'replaced', 'legacy', 'noDelegations'];

export class GuestLinkRefusalError extends Error {
    readonly linkState: GuestLinkRefusal;
    readonly maskedEmail?: string;

    constructor(linkState: GuestLinkRefusal, message: string, maskedEmail?: string) {
        super(message);
        this.name = 'GuestLinkRefusalError';
        this.linkState = linkState;
        this.maskedEmail = maskedEmail;
    }
}

/**
 * Map a thrown callable error to a typed refusal, or null when it is anything
 * else (network, rate limit, validation). Reads `details.linkState` the way the
 * Firebase SDK exposes HttpsError details on the client.
 */
export function guestLinkRefusalFromError(err: unknown): GuestLinkRefusalError | null {
    if (err instanceof GuestLinkRefusalError) return err;
    if (!err || typeof err !== 'object') return null;
    const details = (err as { details?: unknown }).details;
    if (!details || typeof details !== 'object') return null;
    const { linkState, maskedEmail } = details as { linkState?: unknown; maskedEmail?: unknown };
    if (typeof linkState !== 'string' || !REFUSALS.includes(linkState as GuestLinkRefusal)) return null;
    const message = typeof (err as { message?: unknown }).message === 'string'
        ? (err as { message: string }).message
        : 'Länken är inte giltig';
    return new GuestLinkRefusalError(
        linkState as GuestLinkRefusal,
        message,
        typeof maskedEmail === 'string' ? maskedEmail : undefined
    );
}
